import { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { LoginScreen } from '../screens/Auth/LoginScreen';
import { RegisterScreen } from '../screens/Auth/RegisterScreen';
import { ForgotPasswordScreen } from '../screens/Auth/ForgotPasswordScreen';
import { CheckEmailScreen } from '../screens/Auth/CheckEmailScreen';
import { EmailVerificationScreen } from '../screens/Auth/EmailVerificationScreen';
import { AuthActionScreen } from '../screens/Auth/AuthActionScreen';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { AdminProtectedRoute } from '../components/auth/AdminProtectedRoute';
import { RootRedirect } from '../components/auth/RootRedirect';
import { NotFoundRedirect } from '../components/auth/NotFoundRedirect';
import { AppSplash } from '../components/ui/AppSplash';

// ============================================================
// PERF FIX (Brief §6): Lazy-load non-critical screens.
// 
// EAGER (on critical path — never lazy):
//   - Auth screens (login, register, etc.)
//   - HomeScreen (the default post-auth route — must be instant)
//
// LAZY (loaded on demand):
//   - Everything else
//
// Suspense fallback is AppSplash so no blank screen during chunk loading.
// ============================================================

// HOME: Eager-loaded (first screen after auth — must not wait for chunk)
import { HomeScreen } from '../screens/Home/HomeScreen';

// ── Chunk-resilient lazy loader ──
// On chunk load failure (stale deploy), triggers an immediate cache-busted
// hard navigation instead of retrying the same dead hashed URL.
// User stays on Suspense/AppSplash during the reload — never sees ErrorBoundary.
//
// Uses shared policy from chunkRecoveryPolicy.ts (single source of truth
// for keys, limits, and guards — also used by ErrorBoundary.tsx).
import {
  canAutoReloadForChunk,
  isChunkLoadError,
  markChunkReloadInFlight,
  buildChunkReloadUrl,
  logChunkRecoveryBreadcrumb,
} from '../utils/chunkRecoveryPolicy';

/** Module-level flag: once we trigger a reload, suppress further attempts in this render */
let chunkReloadTriggered = false;

function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((error: any) => {
      if (!isChunkLoadError(error)) throw error;

      // Already triggered a reload this render cycle — don't double-fire
      if (chunkReloadTriggered) {
        return new Promise<{ default: React.ComponentType<any> }>(() => {});
      }

      if (canAutoReloadForChunk()) {
        chunkReloadTriggered = true;
        markChunkReloadInFlight();
        logChunkRecoveryBreadcrumb('lazyWithRetry');

        // Cache-busted hard navigation — user stays on AppSplash during this
        window.location.replace(buildChunkReloadUrl());

        // Return promise with safety timeout: if location.replace somehow doesn't
        // navigate (browser blocked, exception, etc.), throw so ErrorBoundary takes over.
        return new Promise<{ default: React.ComponentType<any> }>((_, reject) => {
          setTimeout(() => {
            chunkReloadTriggered = false;
            reject(error);
          }, 2000);
        });
      }

      // Reload guard exhausted — throw so ErrorBoundary handles it
      console.warn('[lazyWithRetry] Chunk reload guard exhausted, deferring to ErrorBoundary');
      throw error;
    })
  );
}

// Secondary screens: Lazy-loaded with chunk retry
const NutritionScreen = lazyWithRetry(() => import('../screens/Nutrition/NutritionScreen').then(m => ({ default: m.NutritionScreen })));
const TargetSettingsScreen = lazyWithRetry(() => import('../screens/Nutrition/TargetSettingsScreen').then(m => ({ default: m.TargetSettingsScreen })));
const WorkoutsScreen = lazyWithRetry(() => import('../screens/Workouts/WorkoutsScreen').then(m => ({ default: m.WorkoutsScreen })));
const MoreScreen = lazyWithRetry(() => import('../screens/More/MoreScreen').then(m => ({ default: m.MoreScreen })));
const AdminScreen = lazyWithRetry(() => import('../screens/More/AdminScreen').then(m => ({ default: m.AdminScreen })));
const BodyMetricsAndGoalsScreen = lazyWithRetry(() => import('../screens/More/BodyMetricsAndGoalsScreen').then(m => ({ default: m.BodyMetricsAndGoalsScreen })));
const BodyStatsScreen = lazyWithRetry(() => import('../screens/More/BodyStatsScreen').then(m => ({ default: m.BodyStatsScreen })));
const GLP1HubScreen = lazyWithRetry(() => import('../screens/More/GLP1HubScreen').then(m => ({ default: m.GLP1HubScreen })));
const SettingsScreen = lazyWithRetry(() => import('../screens/Settings/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const PrivacyPolicyScreen = lazyWithRetry(() => import('../screens/Settings/PrivacyPolicyScreen').then(m => ({ default: m.PrivacyPolicyScreen })));
const HealthDisclaimerScreen = lazyWithRetry(() => import('../screens/Settings/HealthDisclaimerScreen').then(m => ({ default: m.HealthDisclaimerScreen })));
const TermsOfUseScreen = lazyWithRetry(() => import('../screens/Settings/TermsOfUseScreen').then(m => ({ default: m.TermsOfUseScreen })));
const OnboardingScreen = lazyWithRetry(() => import('../screens/Onboarding/OnboardingScreen').then(m => ({ default: m.OnboardingScreen })));
const PremiumSuccessScreen = lazyWithRetry(() => import('../screens/Premium/PremiumSuccessScreen').then(m => ({ default: m.PremiumSuccessScreen })));
const PremiumCancelScreen = lazyWithRetry(() => import('../screens/Premium/PremiumCancelScreen').then(m => ({ default: m.PremiumCancelScreen })));
const PaywallScreen = lazyWithRetry(() => import('../screens/Premium/PaywallScreen').then(m => ({ default: m.PaywallScreen })));
const FunctionalAgeScreen = lazyWithRetry(() => import('../screens/FunctionalAge/FunctionalAgeScreen').then(m => ({ default: m.FunctionalAgeScreen })));
const WeeklyReportScreen = lazyWithRetry(() => import('../screens/More/WeeklyReportScreen').then(m => ({ default: m.WeeklyReportScreen })));

// Dev-only: Messaging Test Harness — excluded from production builds
const MessagingTestPanel = import.meta.env.DEV
  ? lazyWithRetry(() => import('../screens/Debug/MessagingTestPanel').then(m => ({ default: m.MessagingTestPanel })))
  : null;

// Suspense wrapper — shows branded splash (not a blank screen) while chunk loads
function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<AppSplash message="Getting CoreTrak ready…" />}>
      {children}
    </Suspense>
  );
}

function ConsumerDemoRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('demo', '1');
  return <Navigate to={`/?${params.toString()}`} replace />;
}

function PharmacyDemoRedirect() {
  const location = useLocation();
  // Build canonical target — ensure demo=1 and partner=pharmacy are present,
  // preserving any other params (e.g. ?name=Boots, ?src=email)
  const params = new URLSearchParams(location.search);
  params.set('demo', '1');
  params.set('partner', 'pharmacy');
  return <Navigate to={`/?${params.toString()}`} replace />;
}

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  // ---- Auth routes (eager) ----
  {
    path: '/auth/login',
    element: <LoginScreen />,
  },
  {
    path: '/auth/register',
    element: <RegisterScreen />,
  },
  {
    path: '/auth/forgot-password',
    element: <ForgotPasswordScreen />,
  },
  {
    path: '/auth/check-email',
    element: <CheckEmailScreen />,
  },
  {
    path: '/auth/verify-email',
    element: <EmailVerificationScreen />,
  },
  {
    path: '/auth/action',
    element: <AuthActionScreen />,
  },
  // ---- Home (eager — first post-auth screen) ----
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <HomeScreen />
      </ProtectedRoute>
    ),
  },
  // ---- All other routes (lazy) ----
  {
    path: '/nutrition',
    element: (
      <ProtectedRoute>
        <LazyRoute><NutritionScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/nutrition/targets',
    element: (
      <ProtectedRoute>
        <LazyRoute><TargetSettingsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/workouts',
    element: (
      <ProtectedRoute>
        <LazyRoute><WorkoutsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/more',
    element: (
      <ProtectedRoute>
        <LazyRoute><MoreScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <LazyRoute><SettingsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <ProtectedRoute>
        <LazyRoute><PrivacyPolicyScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/health-disclaimer',
    element: (
      <ProtectedRoute>
        <LazyRoute><HealthDisclaimerScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/terms-of-use',
    element: (
      <ProtectedRoute>
        <LazyRoute><TermsOfUseScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/body-metrics',
    element: (
      <ProtectedRoute>
        <LazyRoute><BodyMetricsAndGoalsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/body-goals',
    element: (
      <ProtectedRoute>
        <LazyRoute><BodyMetricsAndGoalsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/body-stats',
    element: (
      <ProtectedRoute>
        <LazyRoute><BodyStatsScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/glp1-hub',
    element: (
      <ProtectedRoute>
        <LazyRoute><GLP1HubScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <LazyRoute><OnboardingScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin',
    element: (
      <AdminProtectedRoute>
        <LazyRoute><AdminScreen /></LazyRoute>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/premium/success',
    element: (
      <ProtectedRoute>
        <LazyRoute><PremiumSuccessScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/premium/cancel',
    element: (
      <ProtectedRoute>
        <LazyRoute><PremiumCancelScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/paywall',
    element: (
      <ProtectedRoute>
        <LazyRoute><PaywallScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/weekly-report',
    element: (
      <ProtectedRoute>
        <LazyRoute><WeeklyReportScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/functional-age',
    element: (
      <ProtectedRoute>
        <LazyRoute><FunctionalAgeScreen /></LazyRoute>
      </ProtectedRoute>
    ),
  },
  {
    // Consumer demo — clean shareable URL
    path: '/demo',
    element: <ConsumerDemoRedirect />,
  },
  {
    // Pharmacy partner demo — clean shareable URL, no query string visible
    // Preserves optional ?name= param: /demo/pharmacy?name=Boots
    path: '/demo/pharmacy',
    element: <PharmacyDemoRedirect />,
  },
  {
    path: '*',
    element: <NotFoundRedirect />,
  },
  // ---- Dev-only: Messaging Test Harness ----
  ...(import.meta.env.DEV && MessagingTestPanel ? [{
    path: '/debug/messaging',
    element: (
      <ProtectedRoute>
        <LazyRoute><MessagingTestPanel /></LazyRoute>
      </ProtectedRoute>
    ),
  }] : []),
];
